import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import {
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    finalize,
    from,
    map,
    Observable,
    of,
    startWith,
    switchMap,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';

import { BoatApiService } from '@bcmApiServices/boat.api-service';
import { CompanyApiService } from '@bcmApiServices/company.api-service';
import { KeyApiService } from '@bcmApiServices/key.api-service';
import { PersonApiService } from '@bcmApiServices/person.api-service';
import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { filterKeyDepositProducts } from '@shared/constants/key-deposit-product-category';
import { TranslationModule } from '@core/translation/translation.module';
import { isUp2boatId, Up2boatIdSelectionService } from '@modules/bcm/@shared/services/up2boat-id-selection.service';
import {
    BOOKING_V2_MIN_SEARCH_LENGTH,
    isBookingV2SearchTooShort,
} from '../../../utils/booking-v2-search-query.util';
import { Boat } from '@shared/models/boat';
import { Company } from '@shared/models/company';
import { Key } from '@shared/models/key';
import { Person } from '@shared/models/person';
import { Product } from '@shared/models/product';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';
import { U2bTabSwitcherComponent, U2bTabSwitcherItem } from '@u2bUi/tab-switcher/u2b-tab-switcher.component';

import {
    BOOKING_V2_KEY_TYPE_LABEL_KEYS,
    BookingV2BoatOption,
    BookingV2KeyAssignment,
    BookingV2KeyCatalogEntry,
    BookingV2KeyType,
    BookingV2KeyUniqueness,
    BookingV2PersonOption,
} from '../../../dtos/booking-v2.dtos';
import { BookingV2Facade } from '../../../services/booking-v2.facade';

type KeyMode = 'catalog' | 'new';

// Lightweight borrower-boat pick (only id + display label are needed here).
interface KeyBoatPick {
    id: number;
    label: string;
}

// A selectable key-deposit product (Schlüsselpfand category).
interface KeyDepositProduct {
    id: number;
    name: string;
    price: number;
    label: string;
    taxRateId: number | null;
    unitId: number | null;
    vatValue: number;
}

const MIN_SEARCH_LENGTH = BOOKING_V2_MIN_SEARCH_LENGTH;
const SEARCH_DEBOUNCE_MS = 300;

@Component({
    selector: 'booking-v2-keys-tab',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        DatePicker,
        IconField,
        InputIcon,
        InputText,
        ProgressSpinner,
        RadioButton,
        Select,
        Textarea,
        TranslationModule,
        U2bDateMaskDirective,
        U2bIconButtonComponent,
        U2bIndicatorCardComponent,
        U2bTabSwitcherComponent,
    ],
    templateUrl: './keys-tab.component.html',
    host: { class: 'block' },
})
export class KeysTabComponent {

    readonly readOnly = input<boolean>(false);

    private readonly facade = inject(BookingV2Facade);
    private readonly keyApi = inject(KeyApiService);

    constructor() {
        // Mark the advanced step dirty while the add form is open & unsaved, so
        // navigating away (tab/step/dialog) prompts to discard.
        effect(() => this.facade.advancedDraftDirty.set(!this.readOnly() && this.showForm()));
    }
    private readonly personApi = inject(PersonApiService);
    private readonly companyApi = inject(CompanyApiService);
    private readonly boatApi = inject(BoatApiService);
    private readonly up2boatIds = inject(Up2boatIdSelectionService);
    private readonly productsApi = inject(ProductsApiService);

    readonly keys = this.facade.keys;
    readonly keyTypeLabelKeys = BOOKING_V2_KEY_TYPE_LABEL_KEYS;

    // ── Key catalog (real backend) ──
    readonly loadingCatalog = signal<boolean>(true);

    readonly catalog = toSignal(
        this.keyApi.getAll().pipe(
            map(list => list.map(toKeyCatalogEntry)),
            catchError(() => of<BookingV2KeyCatalogEntry[]>([])),
            finalize(() => this.loadingCatalog.set(false)),
        ),
        { initialValue: [] as BookingV2KeyCatalogEntry[] },
    );

    readonly modeTabs: U2bTabSwitcherItem<KeyMode>[] = [
        { id: 'catalog', labelKey: 'bookingV2.advanced.keys.mode.catalog' },
        { id: 'new', labelKey: 'bookingV2.advanced.keys.mode.new' },
    ];

    readonly keyTypeOptions: { value: BookingV2KeyType; labelKey: string }[] = [
        { value: 'physical', labelKey: BOOKING_V2_KEY_TYPE_LABEL_KEYS.physical },
        { value: 'qr', labelKey: BOOKING_V2_KEY_TYPE_LABEL_KEYS.qr },
        { value: 'numeric', labelKey: BOOKING_V2_KEY_TYPE_LABEL_KEYS.numeric },
        { value: 'rfid', labelKey: BOOKING_V2_KEY_TYPE_LABEL_KEYS.rfid },
    ];

    readonly showForm = signal<boolean>(false);
    readonly keyMode = signal<KeyMode>('catalog');
    readonly keySearch = signal<string>('');

    readonly filteredCatalog = computed<readonly BookingV2KeyCatalogEntry[]>(() => {
        const q = this.keySearch().trim().toLowerCase();
        const catalog = this.catalog();
        if (!q) {
            return catalog;
        }
        return catalog.filter(k =>
            k.name.toLowerCase().includes(q)
            || (k.identifier?.toLowerCase().includes(q) ?? false)
            || k.typeLabel.toLowerCase().includes(q),
        );
    });

    // ── Key draft (catalog selection or new key) ──
    readonly catalogRef = signal<string | null>(null);
    readonly newType = signal<BookingV2KeyType>('physical');
    // Raw free-text type of a catalog-selected key (null in "new" mode).
    readonly newTypeLabel = signal<string | null>(null);
    readonly newDescription = signal<string>('');
    readonly newIdentifier = signal<string>('');
    readonly newUniqueness = signal<BookingV2KeyUniqueness>('duplicated');

    // ── Deposit (Pfand) ──
    // Catalog mode: inherited from the key (read-only amount). New mode: picked
    // from the Schlüsselpfand product catalog. `depositProductId` null = none.
    readonly depositProductId = signal<number | null>(null);
    readonly depositProductName = signal<string | null>(null);
    readonly depositAmount = signal<number | null>(null);
    // Tax rate / unit / VAT% of the chosen deposit product — carried so the Pfand
    // can be billed as a real invoice position (and shown gross in the summary).
    readonly depositTaxRateId = signal<number | null>(null);
    readonly depositUnitId = signal<number | null>(null);
    readonly depositVatValue = signal<number | null>(null);
    readonly keyFeePayedOn = signal<Date | null>(null);
    readonly keyFeePayedBackOn = signal<Date | null>(null);

    readonly depositProducts = toSignal(
        this.productsApi.getAll().pipe(
            map(list => filterKeyDepositProducts(list).map(toDepositProduct)),
            catchError(() => of<KeyDepositProduct[]>([])),
        ),
        { initialValue: [] as KeyDepositProduct[] },
    );

    // ── Lending details ──
    readonly keyNumber = signal<string>('');
    readonly fromDate = signal<Date | null>(null);
    readonly toDate = signal<Date | null>(null);
    readonly note = signal<string>('');

    // ── Borrower: person ──
    readonly personQuery = signal<string>('');
    readonly loadingPersonSearch = signal<boolean>(false);
    readonly selectedPerson = signal<BookingV2PersonOption | null>(null);

    readonly personSearchResult = toSignal(
        toObservable(this.personQuery).pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            map(q => q.trim()),
            distinctUntilChanged(),
            switchMap(q => {
                if (isUp2boatId(q)) {
                    this.loadingPersonSearch.set(true);
                    return from(this.up2boatIds.resolvePerson(q)).pipe(
                        switchMap(resolution => {
                            // Keine bekannte Up2Boat-ID (z. B. Nachname "Becker"): normale Suche.
                            if (!resolution.found) {
                                return this.searchPersonOptions(q);
                            }
                            const person = resolution.value;
                            if (person?.id) {
                                this.selectPerson(toPersonOption(person));
                                this.personQuery.set('');
                            }
                            return of<readonly BookingV2PersonOption[]>([]);
                        }),
                        catchError(() => of<readonly BookingV2PersonOption[]>([])),
                        finalize(() => this.loadingPersonSearch.set(false)),
                    );
                }
                if (q.length < MIN_SEARCH_LENGTH) {
                    this.loadingPersonSearch.set(false);
                    return of<readonly BookingV2PersonOption[]>([]);
                }
                this.loadingPersonSearch.set(true);
                return this.searchPersonOptions(q);
            }),
        ),
        { initialValue: [] as readonly BookingV2PersonOption[] },
    );

    private searchPersonOptions(q: string): Observable<readonly BookingV2PersonOption[]> {
        const params = new HttpParams().set('filter', q).set('size', '30');
        const persons$ = this.personApi.search(params).pipe(
            map(list => list.map(toPersonOption)),
            startWith<readonly BookingV2PersonOption[]>([]),
            catchError(() => of<readonly BookingV2PersonOption[]>([])),
        );
        const companies$ = this.companyApi.index(params).pipe(
            map(page => (page?.results ?? []).map(toCompanyOption)),
            startWith<readonly BookingV2PersonOption[]>([]),
            catchError(() => of<readonly BookingV2PersonOption[]>([])),
        );
        return combineLatest([persons$, companies$]).pipe(
            map(([persons, companies]): readonly BookingV2PersonOption[] => [...persons, ...companies]),
            finalize(() => this.loadingPersonSearch.set(false)),
        );
    }

    readonly personQueryTooShort = computed(() => isBookingV2SearchTooShort(this.personQuery()));

    readonly personShowNoResults = computed(() =>
        !this.loadingPersonSearch()
        && !this.personQueryTooShort()
        && this.personSearchResult().length === 0,
    );

    // ── Borrower: boat (slim `/index` endpoint, same as the berth step) ──
    readonly boatQuery = signal<string>('');
    readonly loadingBoatSearch = signal<boolean>(false);
    readonly selectedBoat = signal<KeyBoatPick | null>(null);

    readonly boatSearchResult = toSignal(
        toObservable(this.boatQuery).pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            map(q => q.trim()),
            distinctUntilChanged(),
            switchMap(q => {
                if (isUp2boatId(q)) {
                    this.loadingBoatSearch.set(true);
                    return from(this.up2boatIds.resolveBoat(q)).pipe(
                        switchMap(resolution => {
                            // Keine bekannte Up2Boat-ID (z. B. Bootsname): normale Suche.
                            if (!resolution.found) {
                                return this.searchBoatPicks(q);
                            }
                            const boat = resolution.value;
                            if (boat?.id) {
                                this.selectBoat(toBoatPick(boat));
                                this.boatQuery.set('');
                            }
                            return of<readonly KeyBoatPick[]>([]);
                        }),
                        catchError(() => of<readonly KeyBoatPick[]>([])),
                        finalize(() => this.loadingBoatSearch.set(false)),
                    );
                }
                if (q.length < MIN_SEARCH_LENGTH) {
                    this.loadingBoatSearch.set(false);
                    return of<readonly KeyBoatPick[]>([]);
                }
                this.loadingBoatSearch.set(true);
                return this.searchBoatPicks(q);
            }),
        ),
        { initialValue: [] as readonly KeyBoatPick[] },
    );

    private searchBoatPicks(q: string): Observable<readonly KeyBoatPick[]> {
        const params = new HttpParams()
            .set('filter', q)
            .set('page', '0')
            .set('size', '30');
        return this.boatApi.index(params).pipe(
            map(page => (page?.results ?? []).map(toBoatPick)),
            catchError(() => of<readonly KeyBoatPick[]>([])),
            finalize(() => this.loadingBoatSearch.set(false)),
        );
    }

    readonly boatQueryTooShort = computed(() => isBookingV2SearchTooShort(this.boatQuery()));

    readonly boatShowNoResults = computed(() =>
        !this.loadingBoatSearch()
        && !this.boatQueryTooShort()
        && this.boatSearchResult().length === 0,
    );

    // ── Derived ──
    readonly showIdentifierField = computed(() =>
        this.newType() === 'physical' || this.newType() === 'rfid',
    );

    readonly keyChosen = computed(() =>
        this.keyMode() === 'catalog'
            ? this.catalogRef() !== null
            : this.newDescription().trim().length > 0,
    );

    readonly borrowerChosen = computed(() =>
        this.selectedPerson() !== null || this.selectedBoat() !== null,
    );

    // `bcm_person_has_keys` links a key only to a person/boat — a company can be
    // a borrower solely via one of its boats. A company picked as the *sole*
    // borrower (no boat) would be silently dropped by the mapper, so block it
    // here and explain why instead of letting the key vanish at commit.
    readonly companyBorrowerNeedsBoat = computed(() =>
        this.selectedPerson()?.kind === 'company' && this.selectedBoat() === null,
    );

    readonly canAdd = computed(() =>
        this.keyChosen() && this.borrowerChosen() && !this.companyBorrowerNeedsBoat(),
    );

    toggleForm(): void {
        if (this.showForm()) {
            this.closeForm();
        } else {
            this.openForm();
        }
    }

    openForm(): void {
        this.resetDraft();
        // Default the lending window to the booking date range.
        const { from, to } = this.facade.dateRange();
        this.fromDate.set(from);
        this.toDate.set(to);
        // Default the borrower to the booking's main person and boat.
        const person = this.facade.selectedPerson();
        if (person) {
            this.selectedPerson.set({ ...person });
        }
        const boat = this.facade.selectedBoat();
        if (boat) {
            this.selectedBoat.set({ id: boat.id, label: boatOptionLabel(boat) });
        }
        this.showForm.set(true);
    }

    closeForm(): void {
        this.showForm.set(false);
        this.resetDraft();
    }

    setMode(mode: KeyMode): void {
        this.resetKeyDraft();
        this.keyMode.set(mode);
    }

    selectCatalogEntry(entry: BookingV2KeyCatalogEntry): void {
        this.catalogRef.set(entry.id);
        this.newDescription.set(entry.name);
        this.newTypeLabel.set(entry.typeLabel);
        this.newIdentifier.set(entry.identifier ?? '');
        this.newUniqueness.set(entry.uniqueness);
        // Deposit is inherited from the key (read-only); the amount rides along
        // on the catalog entry. No deposit product → no deposit for this key.
        this.depositProductId.set(entry.depositProductId ?? null);
        this.depositProductName.set(entry.depositProductName ?? null);
        this.depositAmount.set(entry.depositAmount ?? null);
        this.depositTaxRateId.set(entry.depositTaxRateId ?? null);
        this.depositUnitId.set(entry.depositUnitId ?? null);
        this.depositVatValue.set(entry.depositVatValue ?? null);
        this.keyFeePayedOn.set(null);
        this.keyFeePayedBackOn.set(null);
    }

    isCatalogSelected(entry: BookingV2KeyCatalogEntry): boolean {
        return this.catalogRef() === entry.id;
    }

    // New-key mode: pick (or clear) the deposit product; amount follows the
    // selected product. Clearing the product drops the deposit entirely.
    onDepositProductChange(id: number | null): void {
        const product = id != null ? this.depositProducts().find(p => p.id === id) ?? null : null;
        this.depositProductId.set(id);
        this.depositProductName.set(product?.name ?? null);
        this.depositAmount.set(product?.price ?? null);
        this.depositTaxRateId.set(product?.taxRateId ?? null);
        this.depositUnitId.set(product?.unitId ?? null);
        this.depositVatValue.set(product?.vatValue ?? null);
        if (id == null) {
            this.keyFeePayedOn.set(null);
            this.keyFeePayedBackOn.set(null);
        }
    }

    onPersonQueryChange(value: string): void {
        this.personQuery.set(value);
    }

    selectPerson(option: BookingV2PersonOption): void {
        this.selectedPerson.set(option);
        this.personQuery.set('');
    }

    removePerson(): void {
        this.selectedPerson.set(null);
        this.personQuery.set('');
    }

    onBoatQueryChange(value: string): void {
        this.boatQuery.set(value);
    }

    selectBoat(option: KeyBoatPick): void {
        this.selectedBoat.set(option);
        this.boatQuery.set('');
    }

    removeBoat(): void {
        this.selectedBoat.set(null);
        this.boatQuery.set('');
    }

    add(): void {
        if (!this.canAdd()) {
            return;
        }
        const isCatalog = this.keyMode() === 'catalog';
        const person = this.selectedPerson();
        const boat = this.selectedBoat();
        const depositProductId = this.depositProductId();
        const depositAmount = this.depositAmount();
        const hasDeposit = depositProductId != null;
        const assignment: BookingV2KeyAssignment = {
            uuid: uuidv4(),
            origin: isCatalog ? 'catalog' : 'new',
            catalogKeyId: isCatalog && this.catalogRef() !== null ? Number(this.catalogRef()) : null,
            keyName: this.newDescription().trim(),
            keyTypeLabel: isCatalog ? this.newTypeLabel() : null,
            keyType: isCatalog ? null : this.newType(),
            keyIdentifier: this.newIdentifier().trim() || null,
            uniqueness: this.newUniqueness(),
            personId: person?.id ?? null,
            personKind: person?.kind ?? null,
            personLabel: person?.displayName ?? null,
            boatId: boat?.id ?? null,
            boatLabel: boat?.label ?? null,
            keyNumber: this.keyNumber().trim() || null,
            from: this.fromDate(),
            to: this.toDate(),
            note: this.note().trim() || null,
            depositProductId,
            depositProductName: hasDeposit ? this.depositProductName() : null,
            depositAmount,
            depositTaxRateId: hasDeposit ? this.depositTaxRateId() : null,
            depositUnitId: hasDeposit ? this.depositUnitId() : null,
            depositVatValue: hasDeposit ? this.depositVatValue() : null,
            keyFeePayedOn: hasDeposit ? this.keyFeePayedOn() : null,
            keyFeePayedBackOn: hasDeposit ? this.keyFeePayedBackOn() : null,
        };
        this.facade.keys.update(list => [...list, assignment]);
        this.closeForm();
    }

    remove(uuid: string): void {
        this.facade.keys.update(list => list.filter(k => k.uuid !== uuid));
    }

    // Resets only the key-selection + deposit part (used when switching mode);
    // borrower and lending window stay intact.
    private resetKeyDraft(): void {
        this.catalogRef.set(null);
        this.keySearch.set('');
        this.newType.set('physical');
        this.newTypeLabel.set(null);
        this.newDescription.set('');
        this.newIdentifier.set('');
        this.newUniqueness.set('duplicated');
        this.depositProductId.set(null);
        this.depositProductName.set(null);
        this.depositAmount.set(null);
        this.depositTaxRateId.set(null);
        this.depositUnitId.set(null);
        this.depositVatValue.set(null);
        this.keyFeePayedOn.set(null);
        this.keyFeePayedBackOn.set(null);
    }

    private resetDraft(): void {
        this.keyMode.set('catalog');
        this.resetKeyDraft();
        this.keyNumber.set('');
        this.fromDate.set(null);
        this.toDate.set(null);
        this.note.set('');
        this.selectedPerson.set(null);
        this.personQuery.set('');
        this.selectedBoat.set(null);
        this.boatQuery.set('');
    }
}

function toKeyCatalogEntry(key: Key): BookingV2KeyCatalogEntry {
    const product = key.product;
    return {
        id: String(key.id),
        name: key.keyName,
        // `keyType` is free text server-side — kept as-is, no enum mapping.
        typeLabel: key.keyType,
        identifier: key.keyId || undefined,
        uniqueness: key.isUnique ? 'unique' : 'duplicated',
        // No reliable stock count on the model — totalCopies stays undefined.
        depositProductId: product?.id ?? undefined,
        depositProductName: product?.name ?? undefined,
        depositAmount: product?.price ?? undefined,
        depositTaxRateId: product?.taxRate?.id ?? undefined,
        depositUnitId: product?.unit?.id ?? undefined,
        depositVatValue: product?.taxRate?.value ?? undefined,
    };
}

function toPersonOption(raw: Person): BookingV2PersonOption {
    return {
        id: raw.id,
        kind: 'person',
        displayName: raw.fullName || `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim(),
        contactPerson: null,
        address: raw.fullAddress || null,
        phone: raw.phone || null,
        mail: raw.mail || null,
        origin: 'existing',
    };
}

function toCompanyOption(raw: Company): BookingV2PersonOption {
    return {
        id: raw.id,
        kind: 'company',
        displayName: raw.fullName || raw.name || '',
        contactPerson: raw.companyContacts?.[0]?.personName || null,
        address: raw.fullAddress || null,
        phone: raw.phone || null,
        mail: raw.mail || null,
        origin: 'existing',
    };
}

function toDepositProduct(p: Product): KeyDepositProduct {
    const price = p.price ?? 0;
    return {
        id: p.id,
        name: p.name,
        price,
        label: `${p.name} · ${price.toFixed(2)} €`,
        taxRateId: p.taxRate?.id ?? null,
        unitId: p.unit?.id ?? null,
        vatValue: p.taxRate?.value ?? 0,
    };
}

function toBoatPick(raw: Boat): KeyBoatPick {
    return { id: raw.id, label: boatLabel(raw) };
}

// Mirrors the legacy assignment dialog's `displayBoatWith`.
function boatLabel(raw: Boat): string {
    return [raw.licensePlate, raw.name, raw.manufacturer || raw.type].filter(Boolean).join(', ');
}

function boatOptionLabel(boat: BookingV2BoatOption): string {
    return [boat.licensePlate, boat.name].filter(Boolean).join(', ') || (boat.name ?? '');
}
